import { Suspense, useEffect, useMemo, useRef } from 'react'
import { useFrame, useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { Text } from '@react-three/drei'

import { selectedProductStore, useStore, showcaseState } from '../../store/uiStore'

// ============================================================
// Carosello circolare (ispirato al RoundCarousel di originkit):
// le foto e i video del prodotto orbitano come card fluttuanti
// dagli angoli arrotondati attorno al modello in vetrina.
// NON SI CLICCANO: le card sono decorative. Ad aprire le foto e i video sono
// le miniature nella scheda, che e' l'unico appiglio e sta dove uno lo cerca —
// due strade per la stessa cosa, una delle quali su oggetti 3D che ruotano e
// scappano sotto il cursore, erano una di troppo.
// ============================================================

// Raggio dell'orbita e lato delle card, tarati quando il prodotto in vetrina
// stava a 1.6 dall'occhio con scala 1.1.
// SI SCALANO CON LA DISTANZA, NON CON LA SCALA DEL PRODOTTO. Prima seguivano
// la scala, e quando il prodotto e' diventato un oggetto da tenere in mano
// (scala da 1.1 a 0.28) le card si sono rimpicciolite a un quarto: illeggibili,
// e infatti e' stato segnalato che non c'era piu' modo di vedere i media.
// Quello che conta e' l'ingombro APPARENTE, che dipende dalla distanza: legate
// a quella, le card restano grandi sullo schermo come erano state tarate,
// qualunque cosa faccia la scala del prodotto.
// RAGGIO STRETTO ATTORNO AL PRODOTTO. Era 0.55 (misurato sulla vetrina di
// allora, prodotto a 1.6 e grande 1.1): le card giravano larghe, e ora che il
// prodotto sta in mano ed e' un oggetto da 0.20 sembravano scollegate da lui.
// Chiesto di farle girare piu' vicine al modello. A 0.38 il raggio effettivo
// viene 0.166 contro un prodotto largo 0.20: le card sfiorano il suo ingombro
// invece di orbitargli lontano.
const RADIUS = 0.38
const CARD = 0.18   // lato massimo di una card
const DIST_RIF = 1.6
const fattore = () => (showcaseState.distanza || DIST_RIF) / DIST_RIF
const SPEED = 0.35  // rad/s di rotazione dell'anello

// Piano con angoli arrotondati e UV rimappate su 0..1 (ShapeGeometry
// le lascia in coordinate locali → la texture uscirebbe sfalsata)
function roundedRectGeometry(w, h, r) {
  const shape = new THREE.Shape()
  const x = -w / 2
  const y = -h / 2
  shape.moveTo(x + r, y)
  shape.lineTo(x + w - r, y)
  shape.quadraticCurveTo(x + w, y, x + w, y + r)
  shape.lineTo(x + w, y + h - r)
  shape.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  shape.lineTo(x + r, y + h)
  shape.quadraticCurveTo(x, y + h, x, y + h - r)
  shape.lineTo(x, y + r)
  shape.quadraticCurveTo(x, y, x + r, y)
  const geometry = new THREE.ShapeGeometry(shape, 8)
  const pos = geometry.attributes.position
  const uv = geometry.attributes.uv
  for (let i = 0; i < pos.count; i++) {
    uv.setXY(i, pos.getX(i) / w + 0.5, pos.getY(i) / h + 0.5)
  }
  return geometry
}

export default function ProductMediaOrbit() {
  const selected = useStore(selectedProductStore)
  if (!selected) return null
  return <OrbitRing key={selected.id} product={selected} />
}

function OrbitRing({ product }) {
  const group = useRef()

  // Stesso ordine del memo `media` di ProductPage (immagini poi video),
  // così l'indice cliccato coincide con quello della lightbox
  // Versione ridotta dalla CDN Shopify: per card così piccole bastano
  // 256px, inutile scaricare le foto originali
  const thumb = (url) => `${url}${url.includes('?') ? '&' : '?'}width=256`
  const media = useMemo(() => {
    const imgs = product.images.edges.map((e) => ({ thumb: thumb(e.node.url), video: false }))
    const vids = (product.media?.edges || [])
      .filter((e) => e.node.mediaContentType === 'VIDEO' && e.node.previewImage?.url)
      .map((e) => ({ thumb: thumb(e.node.previewImage.url), video: true }))
    return [...imgs, ...vids]
    // eslint-disable-next-line
  }, [product])

  useFrame((_, delta) => {
    if (!group.current) return
    group.current.visible = showcaseState.active
    if (!showcaseState.active) return
    group.current.position.set(showcaseState.x, showcaseState.y, showcaseState.z)
    group.current.rotation.y += delta * SPEED
    // comparsa morbida (scala 0 → 1)
    const s = THREE.MathUtils.lerp(group.current.scale.x, 1, 1 - Math.exp(-6 * delta))
    group.current.scale.setScalar(s)
  })

  if (media.length === 0) return null

  return (
    <group ref={group} scale={0.01} visible={false}>
      {media.map((m, i) => (
        <Suspense key={`${m.thumb}-${i}`} fallback={null}>
          <OrbitCard media={m} index={i} count={media.length} />
        </Suspense>
      ))}
    </group>
  )
}

function OrbitCard({ media, index, count }) {
  const ref = useRef()
  const texture = useLoader(THREE.TextureLoader, media.thumb)
  texture.colorSpace = THREE.SRGBColorSpace

  const angle = (index / count) * Math.PI * 2
  const r = RADIUS * fattore()
  const position = [Math.cos(angle) * r, 0, Math.sin(angle) * r]

  // card proporzionata all'immagine, entro CARD di lato massimo
  const { w, h } = useMemo(() => {
    const iw = texture.image?.width || 1
    const ih = texture.image?.height || 1
    const aspect = iw / ih
    const lato = CARD * fattore()
    return aspect >= 1 ? { w: lato, h: lato / aspect } : { w: lato * aspect, h: lato }
  }, [texture])

  // geometria con angoli arrotondati (raggio ~12% del lato corto)
  const geometry = useMemo(() => roundedRectGeometry(w, h, Math.min(w, h) * 0.12), [w, h])
  useEffect(() => () => geometry.dispose(), [geometry])

  const camPos = useRef(new THREE.Vector3()).current
  useFrame(({ camera }) => {
    // billboard: la card guarda sempre la camera (lookAt è world-aware,
    // quindi funziona anche dentro l'anello che ruota)
    camera.getWorldPosition(camPos)
    ref.current?.lookAt(camPos)
  })

  return (
    <group ref={ref} position={position}>
      <mesh geometry={geometry}>
        <meshBasicMaterial map={texture} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      {media.video && (
        <Text
          position={[0, 0, 0.01]}
          fontSize={0.05}
          color="white"
          outlineWidth={0.006}
          outlineColor="#000000aa"
          anchorX="center"
          anchorY="middle"
        >
          ▶
        </Text>
      )}
    </group>
  )
}
